import { onMounted, onUnmounted, reactive, ref, toRefs } from "vue";

const validAxes = ["x", "y"];

export default function useResizer(element, dragHandle, options) {
  const {
    axes = ref(["x", "y"]),
    minHeight = ref(null),
    maxHeight = ref(null),
    minWidth = ref(null),
    maxWidth = ref(null),
  } = toRefs(reactive(options));

  if (axes.value.every((val) => !validAxes.includes(val))) {
    throw new Error(
      'useResizer: must specify a valid axis. Valid axes can be "x" and/or "y"'
    );
  }

  const height = ref(0);
  const width = ref(0);
  const style = ref({});
  const xPosition = ref(0);
  const yPosition = ref(0);

  function getCurrentHeight() {
    return element.value.$el
      ? element.value.$el.offsetHeight
      : element.value.offsetHeight;
  }

  function getCurrentWidth() {
    return element.value.$el
      ? element.value.$el.offsetWidth
      : element.value.offsetWidth;
  }

  function update(xPos = 0, yPos = 0) {
    if (axes.value.includes("x")) {
      const currentWidth = width.value + xPos - xPosition.value;

      const newWidth = Math.max(
        minWidth.value,
        maxWidth.value && currentWidth > maxWidth.value
          ? maxWidth.value
          : currentWidth
      );

      style.value.width = `${newWidth}px`;
    }

    if (axes.value.includes("y")) {
      const currentHeight = height.value + yPos - yPosition.value;

      const newHeight = Math.max(
        minHeight.value,
        maxHeight.value && currentHeight > maxHeight.value
          ? maxHeight.value
          : currentHeight
      );

      style.value.height = `${newHeight}px`;
    }
  }

  function updateMinHeight(newHeight) {
    const currentHeight = getCurrentHeight();
    minHeight.value = newHeight;

    if (currentHeight !== newHeight) {
      update();
    }
  }

  function onMousemove(event) {
    const { x, y } = event;
    update(x, y);
  }

  function onMouseup() {
    document.body.removeEventListener("mousemove", onMousemove, true);
    window.removeEventListener("mouseup", onMouseup, true);
  }

  function onMousedown(event) {
    xPosition.value = event.x;
    yPosition.value = event.y;
    height.value = getCurrentHeight();
    width.value = getCurrentWidth();

    document.body.addEventListener("mousemove", onMousemove, true);
    window.addEventListener("mouseup", onMouseup, true);
    event.preventDefault();
  }

  onMounted(() => {
    dragHandle.value?.addEventListener("mousedown", onMousedown, true);
  });

  onUnmounted(() => {
    dragHandle.value?.removeEventListener("mousedown", onMousedown, true);
  });

  return {
    element,
    dragHandle,
    style,
    updateMinHeight,
  };
}
